import { memo, useMemo } from 'react';

interface SparklineProps {
  /** Price values over time (ascending order by date). */
  data: number[];
  /** Visual variant — watching uses stone, rebooked uses emerald. */
  variant?: 'watching' | 'rebooked';
  /** Height in pixels. */
  height?: number;
  className?: string;
}

/**
 * Abstract sparkline showing price trajectory as a smooth curve.
 * No axes, no labels, no numbers — purely a visual "aliveness" signal.
 *
 * Per the Perch Paradox: this is decorative, not informational.
 * It says "we're monitoring" without exposing mechanism or creating
 * "why didn't you act?" anxiety. Fully hidden from screen readers.
 *
 * Graceful emergence:
 *   0 points → hidden (returns null)
 *   1 point  → single dot
 *   2 points → straight line
 *   3+ points → smooth Catmull-Rom spline
 */
export const Sparkline = memo(function Sparkline({
  data,
  variant = 'watching',
  height = 24,
  className = '',
}: SparklineProps) {
  const path = useMemo(() => {
    if (data.length === 0) return null;

    const filtered = data.filter(v => Number.isFinite(v));
    if (filtered.length === 0) return null;

    return computePath(filtered, height);
  }, [data, height]);

  if (!path) return null;

  const isRebooked = variant === 'rebooked';

  const strokeColor = isRebooked
    ? 'stroke-emerald-500 dark:stroke-emerald-400'
    : 'stroke-stone-300 dark:stroke-stone-600';

  const fillColor = isRebooked
    ? 'fill-emerald-500/10 dark:fill-emerald-400/10'
    : 'fill-none';

  // Single dot for 1 data point
  if (path.type === 'dot') {
    return (
      <svg
        viewBox={`0 0 100 ${height}`}
        className={`w-full ${className}`}
        style={{ height }}
        preserveAspectRatio="none"
        aria-hidden="true"
        role="presentation"
      >
        <circle
          cx={50}
          cy={height / 2}
          r={3}
          className={isRebooked
            ? 'fill-emerald-500 dark:fill-emerald-400'
            : 'fill-stone-300 dark:fill-stone-600'
          }
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      className={`w-full ${className}`}
      style={{ height }}
      preserveAspectRatio="none"
      aria-hidden="true"
      role="presentation"
    >
      {/* Area fill for rebooked variant */}
      {isRebooked && path.area && (
        <path
          d={path.area}
          className={fillColor}
        />
      )}
      {/* Main stroke */}
      <path
        d={path.line}
        fill="none"
        className={strokeColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
});

type PathResult =
  | { type: 'dot' }
  | { type: 'line'; line: string; area: string | null };

/**
 * Compute SVG path from data points.
 * Uses Catmull-Rom spline interpolation for 3+ points to produce
 * a smooth curve without revealing discrete check intervals.
 */
function computePath(data: number[], h: number): PathResult | null {
  if (data.length === 0) return null;
  if (data.length === 1) return { type: 'dot' };

  const w = 100;
  const padding = 3; // vertical padding so stroke doesn't clip
  const minY = Math.min(...data);
  const maxY = Math.max(...data);
  const range = maxY - minY || 1; // avoid division by zero for flat data

  // Map data to x,y coordinates in SVG space
  const points = data.map((val, i) => ({
    x: (i / (data.length - 1)) * w,
    // Invert Y (SVG y=0 is top). Map to padded range.
    y: padding + ((1 - (val - minY) / range) * (h - padding * 2)),
  }));

  if (data.length === 2) {
    const line = `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;
    return { type: 'line', line, area: null };
  }

  // Catmull-Rom to cubic bezier conversion for smooth curves
  const line = catmullRomPath(points);
  // Area path: line path + close along bottom
  const area = `${line} L${points[points.length - 1].x},${h} L${points[0].x},${h} Z`;

  return { type: 'line', line, area };
}

interface Point { x: number; y: number }

/**
 * Convert a series of points to a smooth SVG path using Catmull-Rom splines.
 * Tension factor of 0.5 gives a natural-looking curve.
 */
function catmullRomPath(points: Point[], tension = 0.5): string {
  if (points.length < 2) return '';

  const d: string[] = [`M${points[0].x.toFixed(2)},${points[0].y.toFixed(2)}`];

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    // Catmull-Rom to cubic bezier control points
    const cp1x = p1.x + (p2.x - p0.x) / (6 / tension);
    const cp1y = p1.y + (p2.y - p0.y) / (6 / tension);
    const cp2x = p2.x - (p3.x - p1.x) / (6 / tension);
    const cp2y = p2.y - (p3.y - p1.y) / (6 / tension);

    d.push(`C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`);
  }

  return d.join(' ');
}
